import { useTranslation } from "react-i18next";
import { Flex } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import { Checkbox } from "../../../components/fields/base/Checkbox";
import { NumberInput } from "../../../components/fields/base/NumberInput";
import {
  CUMULATIVE_NEGATION_THESHOLD_DEFAULT,
  MAX_ACOS_NEGATION_THESHOLD_DEFAULT,
} from "../../../components/core/blocks/AdPlan/constants";

export type BulkAutoNegationSettingsFormData = {
  autoNegationEnabled: boolean;
  maxAcosNegationThreshold: number | null;
  cumulativeNegationThreshold: number | null;
};

type Props = {
  formData: BulkAutoNegationSettingsFormData;
  setFormData: (data: BulkAutoNegationSettingsFormData) => void;
};

export const DEFAULT_AUTO_NEGATION_SETTINGS_FORM_DATA = {
  autoNegationEnabled: false,
  maxAcosNegationThreshold: null,
  cumulativeNegationThreshold: null,
};

const BulkAutoNegationSettingsForm = ({ formData, setFormData }: Props) => {
  const { t } = useTranslation(["advertising"]);

  const maxAcosThresholdPercent = ((formData.maxAcosNegationThreshold ?? 0) / 100) * 25;
  const cumulativeThresholdPercent = ((formData.cumulativeNegationThreshold ?? 0) / 100) * 25;

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      className={css({ display: "flex", flexDirection: "column", alignItems: "center" })}
    >
      <p className={css({ maxWidth: "75%", padding: "large", textAlign: "center" })}>
        {t(
          ($) =>
            $.advertising[
              "Customize auto negation settings using the fields below. Only media plans with an auto campaign will be updated."
            ],
        )}
      </p>
      <Flex justifyContent={"center"} width={"100%"} paddingBottom={"large"}>
        <Checkbox
          value={formData.autoNegationEnabled}
          onChange={(checked) => {
            const updatedFormData =
              checked ?
                {
                  maxAcosNegationThreshold: MAX_ACOS_NEGATION_THESHOLD_DEFAULT,
                  cumulativeNegationThreshold: CUMULATIVE_NEGATION_THESHOLD_DEFAULT,
                  autoNegationEnabled: true,
                }
              : DEFAULT_AUTO_NEGATION_SETTINGS_FORM_DATA;
            setFormData(updatedFormData);
          }}
          label={t(($) => $.advertising["Auto Negation"])}
        />
      </Flex>
      {formData.autoNegationEnabled ?
        <>
          <Flex className={css({ gap: "xlarge", alignItems: "flex-end", paddingX: "large" })}>
            <NumberInput
              onChange={(value) => {
                const updatedFormData = { ...formData, maxAcosNegationThreshold: value };
                setFormData(updatedFormData);
              }}
              value={formData.maxAcosNegationThreshold ?? 0}
              label={t(($) => $.advertising["Optimal ACOS Range High Negation Threshold"])}
              css={{
                flex: "2",
              }}
              isDisabled={!formData.autoNegationEnabled}
              minValue={0}
            />
            <NumberInput
              onChange={(value) => {
                const updatedFormData = { ...formData, cumulativeNegationThreshold: value };
                setFormData(updatedFormData);
              }}
              value={formData.cumulativeNegationThreshold ?? 0}
              label={t(($) => $.advertising["Cumulative Negation Threshold"])}
              css={{
                flex: "2",
              }}
              isDisabled={!formData.autoNegationEnabled}
              minValue={0}
            />
          </Flex>
          <p className={css({ maxWidth: "75%", paddingTop: "xlarge", textAlign: "center" })}>
            {t(
              ($) =>
                $.advertising[
                  "For example, if the optimal ACOS range limit is set to 25%, keywords above {maxAcosThresholdPercent}% ACOS will be negated after they accumulate sufficient spend. If the optimal ACOS range limit is set to 25% and ACOS is below {cumulativeThresholdPercent}% auto negation will not run."
                ],
              { maxAcosThresholdPercent, cumulativeThresholdPercent },
            )}
          </p>
        </>
      : null}
    </form>
  );
};

export default BulkAutoNegationSettingsForm;
